import styled, {css} from "styled-components";
import PopupCloseButton from "@FRAMEWORK/ui/desktop/button/PopupCloseButton";
import {useRef, useState, useEffect, MutableRefObject} from "react";
import {PopupProps} from "@FRAMEWORK/ui/types/d";

interface Props extends PopupProps {
	size?: "small" | "regular" | "large" | "full";
	paddingTop?: boolean;
	show?: boolean;
	animate?: "basic" | "slideIn";
	height?: number;
	close?: () => void;
	ref?: MutableRefObject<HTMLElement>;
}

const Wrap = styled.div<{
	isScroll?: boolean;
	animate?: "basic" | "slideIn";
	show?: boolean;
	size?: string;
}>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 101;
	display: flex;
	align-items: ${props => (props.isScroll ? "flex-start" : "center")};
	justify-content: center;
	width: 100%;
	height: 100%;
	padding: 50px 0;
	background-color: rgba(0, 0, 0, 0.6);
	overflow-y: ${props => (props.isScroll ? "scroll" : "hidden")};
	box-sizing: border-box;
	opacity: 0;
	visibility: ${props => (props.show ? "visible" : "hidden")};
	transition: visibility 0.3s ease-out;

	animation: ${props =>
		props.show
			? css`
				fadeIn 0.3s ease forwards
			`
			: css`
				fadeOut 0.3s ease forwards
			`};

	@keyframes fadeIn {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	@keyframes fadeOut {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}

	//출발일 보기 팝업
	${props =>
		props.size === "full" &&
		`
		padding-top: 72px;
		padding-bottom: 0;
	`}

	@media print {
		position: relative;
		top: auto;
		left: auto;
		right: auto;
		bottom: auto;
		width: 210mm;
		height: auto;
		margin: 0;
		padding: 0;
		overflow: visible;
		visibility: visible;
		align-items: flex-start;
		justify-content: flex-start;
		background-color: transparent;
		opacity: 1;
	}
`;

const FullContent = styled.div<{size?: string}>`
	${props =>
		props.size === "full" &&
		`
	width: 100%;
	height: 100%;
	background-color: #fff;
	> div {
		margin: 0 auto;
	}
	`}
`;

const Content = styled.div<{
	size?: "small" | "regular" | "large" | "full";
	top?: boolean;
	animate?: "basic" | "slideIn";
	show?: boolean;
	height?: number;
}>`
	position: relative;
	z-index: 105;
	display: flex;
	flex-direction: column;
	flex: 0 0 auto;
	border-radius: 16px;
	padding-top: ${props => (props.top ? "50px" : "0")};
	background-color: #fff;
	visibility: ${props => (props.show ? "visible" : "hidden")};
	transition: visibility 0.3s ease-out;
	animation: ${props => {
		if (props.animate === "slideIn" && props.show) {
			return css`
				slideIn 0.3s cubic-bezier(0, 0, 0.2, 1)
			`;
		}
		if (props.animate === "slideIn" && !props.show) {
			return css`
				slideOut 0.3s cubic-bezier(0, 0, 0.2, 1)
			`;
		}
	}};

	${props => {
		if (props.size === "small") {
			return css`
				width: 500px;
				min-width: 500px;
			`;
		}
		if (props.size === "regular") {
			return css`
				width: 800px;
				min-width: 800px;
			`;
		}
		if (props.size === "large") {
			return css`
				width: 990px;
				min-width: 990px;
			`;
		}
		if (props.size === "full") {
			return css`
				height: 100%;
				padding-top: 0;
			`;
		}
	}}

	@keyframes slideIn {
		0% {
			transform: translateY(100%);
		}
		100% {
			transform: translateY(0);
		}
	}

	@keyframes slideOut {
		0% {
			transform: translateY(0);
		}
		100% {
			transform: translateY(100%);
		}
	}

	@media print {
		display: block;
	}
`;

const Header = styled.div<{
	size?: "small" | "regular" | "large" | "full";
	fixed?: boolean;
	top?: boolean;
}>`
	display: flex;
	flex: 0 0 70px;
	width: ${props => {
		if (props.size === "small") {
			return "500px";
		}
		if (props.size === "regular") {
			return "800px";
		}
		if (props.size === "large") {
			return "990px";
		}
	}};

	> div {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex: 1 1 auto;
		margin: 0 40px;
		border-bottom: 1px solid var(--emp-color);
	}

	${props =>
		props.size === "full" &&
		`
		position: sticky;
		top: -244px;
		width: 1200px;
		padding-right: 15px;
		margin: 0 auto;
		> div {
			margin: 0;
		}
	`}

	${props =>
		props.top &&
		`
			position: fixed;
			top: 0;
			height: 70px;
			background: #fff;
			z-index: 1;
		`} 

	@media print {
		display: none;
	}
`;

const Container = styled.div<{
	paddingTop: boolean;
	fixed?: boolean;
	animate?: "basic" | "slideIn";
	size?: string;
}>`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	padding: ${props => (props.paddingTop ? "40px;" : "0 40px 40px;")};
	overflow-y: auto;

	${props =>
		props.size === "full" &&
		`
		padding: 0;
		margin-top: 0;
	`}

	@media print {
		padding: 0;
		overflow: visible;
	}
`;

const Title = styled.h3`
	display: -webkit-box;
	padding-right: 30px;
	font-size: 22px;
	color: var(--emp-color);
	font-family: "PT-medium";
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: normal;
	word-wrap: break-word;
	word-break: break-all;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	line-clamp: 1;
`;

const useResizeObserver = (ref: MutableRefObject<HTMLDivElement>) => {
	const [dimension, setDimentsion] = useState(0);

	useEffect(() => {
		const observerTarget = ref.current ? ref.current : document.querySelector(".popup_content");
		const resizeObserver = new ResizeObserver(entries => {
			entries.forEach(entry => {
				setDimentsion(entry.target.clientHeight);
			});
		});
		resizeObserver.observe(observerTarget);
		return () => {
			resizeObserver.unobserve(observerTarget);
		};
	}, []);

	return dimension;
};

const PopupContainer = ({
	title,
	children,
	size = "small",
	paddingTop = true,
	close,
	animate = "basic",
	show = true,
	height,
}: Props) => {
	const wrapRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const [fixed, setFixed] = useState(false);
	const [top, setTop] = useState(false);

	const [wrapHeight, setWrapHeight] = useState(0);
	const [contentHeight, setContentHeight] = useState(0);
	const [isScroll, setIsScroll] = useState(false);

	const dimension = useResizeObserver(contentRef) | contentHeight;

	useEffect(() => {
		setIsScroll(wrapHeight - 20 < dimension);
	}, [dimension]);

	useEffect(() => {
		if (wrapRef.current && contentRef.current) {
			setWrapHeight(wrapRef.current?.offsetHeight);
			setContentHeight(dimension);
			setFixed(wrapHeight < dimension);
		}
	}, [wrapRef.current]);

	const onScroll = () => {
		if (wrapRef.current) {
			if (wrapRef.current.scrollTop > 10) {
				setTop(true);
			} else {
				setTop(false);
			}
		}
	};

	useEffect(() => {
		if (show) {
			document.body.style.overflowY = "hidden";
		}
		return () => {
			const showCount = document.querySelectorAll("[data-show='true']")?.length || 0;
			if (showCount > 0) return;
			document.body.style.removeProperty("overflow-y");
		};
	}, [show]);
	return (
		<Wrap
			ref={wrapRef}
			data-show={String(show)}
			show={show}
			animate={animate}
			isScroll={isScroll}
			onScroll={onScroll}
			size={size}
		>
			<FullContent size={size}>
				<Content
					className="popup_content"
					ref={contentRef}
					show={show}
					animate={animate}
					size={size}
					top={top}
					height={height || wrapHeight}
				>
					<Header size={size} fixed={fixed} top={top}>
						<div>
							<Title>{title || "안내"}</Title>
							<PopupCloseButton onClose={close} />
						</div>
					</Header>
					<Container animate={animate} paddingTop={paddingTop ? true : paddingTop} fixed={fixed} size={size}>
						{children}
					</Container>
				</Content>
			</FullContent>
		</Wrap>
	);
};

export default PopupContainer;
